import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  TextField,
  Chip
} from '@mui/material';
import {
  X,
  Download,
  PlusCircle,
  RefreshCw,
  ListPlus,
  Music2,
  FileText,
  AlertCircle
} from 'lucide-react';
import {
  MAX_PLAYLIST_NAME_LENGTH,
  sanitizePlaylistName,
  isValidPlaylistChar
} from '../../utils/playlistUtils';

export default function ImportPlaylistDialog({
  open,
  onClose,
  importData,
  currentPlaylistName,
  onOverwriteCurrent,
  onOverwrite,
  onAppendCurrent,
  onAppend,
  onCreateNewPlaylist,
  onCreateNew,
  existingPlaylistNames = []
}) {
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [nameError, setNameError] = useState('');

  const handleOverwrite = onOverwriteCurrent || onOverwrite;
  const handleAppend = onAppendCurrent || onAppend;
  const handleCreate = onCreateNewPlaylist || onCreateNew;

  useEffect(() => {
    if (importData && importData.name) {
      let candidate = sanitizePlaylistName(importData.name).trim();
      if (!candidate) candidate = 'Lista importada';

      // Si ya existe una lista con ese nombre, sugerir un sufijo (1), (2), etc.
      let counter = 1;
      let uniqueName = candidate;
      const lowerExisting = existingPlaylistNames.map(n => n.toLowerCase());
      while (lowerExisting.includes(uniqueName.toLowerCase())) {
        const suffix = ` (${counter})`;
        uniqueName = sanitizePlaylistName(candidate.slice(0, MAX_PLAYLIST_NAME_LENGTH - suffix.length) + suffix);
        counter++;
      }

      setNewPlaylistName(uniqueName);
      setNameError('');
    }
  }, [importData, existingPlaylistNames]);

  if (!importData) return null;

  const handleNameKeyDown = (e) => {
    // Permitir teclas de control: Backspace, Delete, flechas, Tab, Enter
    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key)) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCreateNew();
      }
      return;
    }

    // Bloquear combinaciones con Ctrl/Cmd excepto copiar/pegar
    if (e.ctrlKey || e.metaKey) return;

    // Bloquear si se supera el límite de 30 caracteres
    if (e.target.value.length >= MAX_PLAYLIST_NAME_LENGTH) {
      e.preventDefault();
      return;
    }

    // Bloquear caracteres no permitidos
    if (!isValidPlaylistChar(e.key)) {
      e.preventDefault();
    }
  };

  const handleNameChange = (e) => {
    const clean = sanitizePlaylistName(e.target.value);
    setNewPlaylistName(clean);

    if (!clean.trim()) {
      setNameError('El nombre no puede estar vacío');
    } else if (existingPlaylistNames.some(n => n.toLowerCase() === clean.trim().toLowerCase())) {
      setNameError('Ya existe una lista con este nombre');
    } else {
      setNameError('');
    }
  };

  const handleCreateNew = () => {
    const clean = newPlaylistName.trim();
    if (!clean) {
      setNameError('Ingresa un nombre para la nueva lista');
      return;
    }
    if (existingPlaylistNames.some(n => n.toLowerCase() === clean.toLowerCase())) {
      setNameError('Ya existe una lista con este nombre');
      return;
    }
    if (handleCreate) {
      handleCreate(clean, importData.items);
    }
    onClose();
  };

  const songCount = Array.isArray(importData.items) ? importData.items.length : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: '#0f081d !important',
          backgroundColor: '#0f081d !important',
          backdropFilter: 'none !important',
          border: '1px solid rgba(0, 240, 255, 0.4)',
          borderRadius: 3,
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.98), 0 0 45px rgba(0, 240, 255, 0.25)',
          color: '#fff',
          overflow: 'hidden'
        }
      }}
    >
      {/* Encabezado con margen inferior */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          pb: 2,
          pt: 2.2,
          px: 3,
          mb: 2,
          background: '#150a26'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.25), rgba(168, 85, 247, 0.25))',
              border: '1px solid rgba(0, 240, 255, 0.4)'
            }}
          >
            <Download size={20} color="#00f0ff" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', fontSize: '1.15rem' }}>
              Importar Lista de Reproducción
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(0, 240, 255, 0.85)', fontWeight: 500 }}>
              ¿Cómo deseas incorporar estas canciones a Garaoke?
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.6)',
            '&:hover': { color: '#ff0055', transform: 'rotate(90deg)', transition: 'all 0.25s ease' }
          }}
        >
          <X size={18} />
        </IconButton>
      </DialogTitle>

      {/* Contenido */}
      <DialogContent sx={{ py: 3, px: 3 }}>
        {/* Banner de resumen del archivo */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <FileText size={22} color="#c084fc" />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffffff', fontSize: '0.92rem' }}>
                {importData.name || 'Archivo de playlist'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Destino actual seleccionado: <strong style={{ color: '#00f0ff' }}>{currentPlaylistName}</strong>
              </Typography>
            </Box>
          </Box>
          <Chip
            icon={<Music2 size={14} color="#00f0ff" />}
            label={`${songCount} ${songCount === 1 ? 'canción' : 'canciones'}`}
            size="small"
            sx={{
              background: 'rgba(0, 240, 255, 0.15)',
              color: '#00f0ff',
              border: '1px solid rgba(0, 240, 255, 0.3)',
              fontWeight: 700
            }}
          />
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgba(255, 255, 255, 0.9)', mb: 1.8 }}>
          Elige una acción:
        </Typography>

        {/* Opción 1: Sobreescribir */}
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            background: 'rgba(255, 45, 155, 0.05)',
            border: '1px solid rgba(255, 45, 155, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'rgba(255, 45, 155, 0.1)',
              borderColor: 'rgba(255, 45, 155, 0.45)'
            }
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ff2d9b' }}>
              1. Sobreescribir lista actual
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block' }}>
              Reemplaza todas las canciones de <strong>"{currentPlaylistName}"</strong> por las {songCount} canciones del archivo.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => {
              if (handleOverwrite) {
                handleOverwrite(importData.items);
              }
              onClose();
            }}
            startIcon={<RefreshCw size={15} />}
            sx={{
              borderColor: 'rgba(255, 45, 155, 0.5)',
              color: '#ff2d9b',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              flexShrink: 0,
              '&:hover': {
                borderColor: '#ff2d9b',
                background: 'rgba(255, 45, 155, 0.15)'
              }
            }}
          >
            Sobreescribir
          </Button>
        </Box>

        {/* Opción 2: Agregar al final */}
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            background: 'rgba(0, 240, 120, 0.05)',
            border: '1px solid rgba(0, 240, 120, 0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'rgba(0, 240, 120, 0.1)',
              borderColor: 'rgba(0, 240, 120, 0.45)'
            }
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#00f078' }}>
              2. Agregar al final de la lista actual
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block' }}>
              Conserva las canciones existentes en <strong>"{currentPlaylistName}"</strong> y anexa las {songCount} nuevas canciones al final.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => {
              if (handleAppend) {
                handleAppend(importData.items);
              }
              onClose();
            }}
            startIcon={<PlusCircle size={15} />}
            sx={{
              borderColor: 'rgba(0, 240, 120, 0.5)',
              color: '#00f078',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8rem',
              flexShrink: 0,
              '&:hover': {
                borderColor: '#00f078',
                background: 'rgba(0, 240, 120, 0.15)'
              }
            }}
          >
            Agregar al final
          </Button>
        </Box>

        {/* Opción 3: Crear nueva lista propia */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: 'rgba(0, 240, 255, 0.06)',
            border: '1px solid rgba(0, 240, 255, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#00f0ff' }}>
              3. Crear como nueva lista propia
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block' }}>
              Crea una lista independiente con estas {songCount} canciones y la activa inmediatamente en tu panel.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              fullWidth
              value={newPlaylistName}
              onChange={handleNameChange}
              onKeyDown={handleNameKeyDown}
              error={Boolean(nameError)}
              helperText={nameError || `${newPlaylistName.length}/${MAX_PLAYLIST_NAME_LENGTH} caracteres (letras, números, espacios, guiones)`}
              placeholder="Nombre de la nueva lista..."
              inputProps={{ maxLength: MAX_PLAYLIST_NAME_LENGTH }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.86rem',
                  borderColor: 'rgba(0, 240, 255, 0.3)',
                  '& fieldset': { borderColor: 'rgba(0, 240, 255, 0.3)' },
                  '&:hover fieldset': { borderColor: '#00f0ff' },
                  '&.Mui-focused fieldset': { borderColor: '#00f0ff' }
                },
                '& .MuiFormHelperText-root': {
                  color: nameError ? '#ff4d6d' : 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.72rem'
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleCreateNew}
              disabled={Boolean(nameError) || !newPlaylistName.trim()}
              startIcon={<ListPlus size={16} />}
              sx={{
                background: 'linear-gradient(135deg, #00f0ff 0%, #7000ff 100%)',
                color: '#0a0d18',
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '0.82rem',
                flexShrink: 0,
                py: 1,
                boxShadow: '0 0 15px rgba(0, 240, 255, 0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #33f3ff 0%, #851aff 100%)',
                  boxShadow: '0 0 20px rgba(0, 240, 255, 0.6)'
                },
                '&.Mui-disabled': {
                  opacity: 0.45,
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              Crear y Cargar
            </Button>
          </Box>
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          py: 1.5,
          px: 3,
          background: 'rgba(10, 13, 22, 0.6)'
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { color: '#fff', background: 'rgba(255, 255, 255, 0.06)' }
          }}
        >
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
